import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { pricingPlans } from '../data/mock';

const PricingSection = ({ onOpenForm }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % pricingPlans.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + pricingPlans.length) % pricingPlans.length);
  };

  useEffect(() => {
    if (isMobile && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.offsetWidth;
      container.scrollTo({
        left: currentSlide * cardWidth,
        behavior: 'smooth'
      });
    }
  }, [currentSlide, isMobile]);

  return (
    <section id="pricing" className="py-20 bg-accent/30">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wide">Pricing Plans</span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
              Transparent Pricing for Every Business
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Choose the package that best fits your trademark registration needs. All plans include expert filing and USPTO submission.
            </p>
          </div>

          {/* Desktop Grid View */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-lg shadow-lg overflow-hidden flex flex-col ${
                  plan.recommended ? 'ring-4 ring-secondary transform scale-105' : ''
                }`}
              >
                {/* Header */}
                <div className={`p-6 text-center ${plan.recommended ? 'bg-secondary text-white' : 'bg-primary text-white'}`}>
                  {plan.recommended && (
                    <div className="bg-white text-secondary text-xs font-bold px-3 py-1 rounded-full inline-block mb-2">
                      RECOMMENDED
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-2 text-white">{plan.name}</h3>
                  <div className="text-4xl font-bold mb-1 text-white">{plan.price}</div>
                  <div className="text-sm opacity-90 text-white">+ Federal Filing Fee</div>
                </div>

                {/* Features - Scrollable */}
                <div className="flex-1 p-6 overflow-y-auto" style={{ minHeight: '280px', maxHeight: '400px' }}>
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Check className="text-secondary flex-shrink-0 mt-1" size={18} />
                        <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Footer */}
                <div className="p-6 bg-gray-50 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center mb-4">{plan.note}</p>
                  <button
                    onClick={onOpenForm}
                    className={`w-full py-3 rounded-md font-semibold transition-all shadow-md hover:shadow-lg ${
                      plan.recommended
                        ? 'bg-secondary text-white hover:bg-secondary/90'
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                  >
                    Choose {plan.name}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Slider View */}
          <div className="md:hidden relative">
            <div
              ref={scrollContainerRef}
              className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {pricingPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="flex-shrink-0 w-full snap-center px-2"
                >
                  <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${
                    plan.recommended ? 'ring-4 ring-secondary' : ''
                  }`}>
                    {/* Header */}
                    <div className={`p-6 text-center ${plan.recommended ? 'bg-secondary text-white' : 'bg-primary text-white'}`}>
                      {plan.recommended && (
                        <div className="bg-white text-secondary text-xs font-bold px-3 py-1 rounded-full inline-block mb-2">
                          RECOMMENDED
                        </div>
                      )}
                      <h3 className="text-2xl font-bold mb-2 text-white">{plan.name}</h3>
                      <div className="text-4xl font-bold mb-1 text-white">{plan.price}</div>
                      <div className="text-sm opacity-90 text-white">+ Federal Filing Fee</div>
                    </div>

                    {/* Features - Scrollable */}
                    <div className="p-6 overflow-y-auto" style={{ minHeight: '280px', maxHeight: '400px' }}>
                      <ul className="space-y-3">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Check className="text-secondary flex-shrink-0 mt-1" size={18} />
                            <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Footer */}
                    <div className="p-6 bg-gray-50 border-t border-gray-200">
                      <p className="text-xs text-gray-500 text-center mb-4">{plan.note}</p>
                      <button
                        onClick={onOpenForm}
                        className={`w-full py-3 rounded-md font-semibold transition-all shadow-md ${
                          plan.recommended
                            ? 'bg-secondary text-white hover:bg-secondary/90'
                            : 'bg-primary text-white hover:bg-primary/90'
                        }`}
                      >
                        Choose {plan.name}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-secondary hover:text-white transition-all"
              aria-label="Previous plan"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-secondary hover:text-white transition-all"
              aria-label="Next plan"
            >
              <ChevronRight size={24} />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center space-x-2 mt-6">
              {pricingPlans.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    currentSlide === index ? 'bg-secondary w-8' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to plan ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-12 bg-white rounded-lg p-8 shadow-md">
            <h3 className="text-xl font-bold text-primary mb-4 text-center">What's Included in All Plans</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-secondary text-2xl font-bold mb-2">✓</div>
                <div className="text-sm text-gray-700">Expert Case Review & Filing</div>
              </div>
              <div>
                <div className="text-secondary text-2xl font-bold mb-2">✓</div>
                <div className="text-sm text-gray-700">USPTO Submission & Tracking</div>
              </div>
              <div>
                <div className="text-secondary text-2xl font-bold mb-2">✓</div>
                <div className="text-sm text-gray-700">Professional Support Team</div>
              </div>
            </div>
            <p className="text-xs text-gray-500 text-center mt-6">
              *Government filing fees are separate and paid directly to USPTO (currently $250-$350 per class)
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
